import { Text } from "@/components/ui/baseUi/text"
import type { BoxProgramDetailsEntity } from "@/modules/boxProgrammation/entities/boxProgramEntity"
import type { BoxSessionDetailsEntity } from "@/modules/boxSlotSession/entities/boxSessionEntity"
import { cn } from "@/modules/lib/utils"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { CircleDashedIcon } from "@images/svgIcons/circleDashed"
import { ForbiddenIcon } from "@images/svgIcons/forbiddenIcon"
import { BoxProgrammationSessionEvent } from "@modules/boxProgrammationSessions/components/boxProgrammationSessionEvent"
import { type BoxSlotItemEntity } from "@modules/boxSlot/entities/boxSlotEntity"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { AddEventButton } from "@modules/calendar/components/addEventButton"
import { word } from "@modules/core/utils/i18n"
import { dateSeparator, defaultDateFormat } from "@modules/utils/dateUtils"
import dayjs, { Dayjs } from "dayjs"
import styled from "styled-components"

export function BoxProgrammationSessionsCalendarDayColumn({
  program,
  sessions,
  slots,
  date,
  className,
  ...props
}: {
  program: BoxProgramDetailsEntity
  sessions: BoxSessionDetailsEntity[]
  slots: BoxSlotItemEntity[]
  date: Dayjs | undefined
  className?: string
} & React.ComponentProps<"td">) {
  const canManageProgramming = useBoxStaffPermission(BoxStaffPermissions.MANAGE_PROGRAMMING)
  const { navigateToSessionForm } = useSessionSidebar()

  const onAddSessionClick = async () => {
    if (slots.length === 0 || !slots[0]) return
    await navigateToSessionForm(program, undefined, date?.format(defaultDateFormat), slots[0].id)
  }

  return (
    <Container
      id={`${dateSeparator}`}
      className={cn(
        "dayCell pt-2 pr-0 pl-0 lg:pr-1 lg:pl-1 " + (dayjs(date).isSame(dayjs(), "date") ? "today" : "future"),
        className,
      )}
      style={{
        backgroundColor: typeof slots === "undefined" || slots.length === 0 ? "var(--neutral-50)" : "transparent",
      }}
      {...props}
    >
      <div style={{ minHeight: "auto" }}>
        {sessions.map(
          (session) =>
            slots[0] && <BoxProgrammationSessionEvent key={session.id} session={session} boxSlotId={slots[0].id} />,
        )}
      </div>
      {sessions.length === 0 &&
        (slots && slots.length > 0 ? (
          <div className="mt-1 flex w-full flex-row items-center justify-center gap-2">
            <CircleDashedIcon color="currentColor" className="text-textDefault-body" />
            <Text fontStyle="BS" className="w-6/12 text-center">
              {word("box.programmation.calendar.emptySlots")}
            </Text>
          </div>
        ) : (
          <div className="mt-1 flex w-full flex-row items-center justify-center gap-2">
            <ForbiddenIcon color="currentColor" className="text-neutral-500" />
            <Text fontStyle="BS" className="w-6/12 text-center text-textDefault-body">
              {word("box.programmation.calendar.noSlot")}
            </Text>
          </div>
        ))}
      {sessions.length === 0 && (typeof slots === "undefined" || slots.length > 0) && (
        <>
          {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
          {canManageProgramming && (
            <AddEventButton
              onClick={() => {
                if (date) void onAddSessionClick()
              }}
              displayText
              addEventTitle={word("planning.addSession.button")}
            />
          )}
        </>
      )}
    </Container>
  )
}

const Container = styled.td`
  vertical-align: top;
  text-align: left;
  word-break: break-word;
  &.past {
    cursor: not-allowed;
  }
  &.past .add {
    display: none;
  }
  > .add {
    opacity: 0;
    transition: opacity 0.2s ease;
    cursor: pointer;
    margin-top: 20px;
    gap: 10px;
  }
  &:not(.past):not(.dragging):hover .add {
    opacity: 1;
  }
`
